import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useLocation } from '@tanstack/react-router'
import { AlertCircle, Eye, EyeOff } from 'lucide-react'
import { CustomerLoginInput } from '@buddys-choice/shared'
import type { CustomerLoginInput as CustomerLoginInputType } from '@buddys-choice/shared'
import { apiPost } from '../lib/api'
import { useAuth, type User as AuthUser } from '../context/AuthContext'
import { CustomerAuthLayout } from '../layouts/CustomerAuthLayout'

export const CustomerLoginPage: React.FC = () => {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CustomerLoginInputType>({
    resolver: zodResolver(CustomerLoginInput),
    defaultValues: { phone: '', password: '' },
  })

  const redirectTo = (() => {
    const search = (location.search ?? {}) as Record<string, unknown>
    return typeof search.redirect === 'string' && search.redirect.startsWith('/')
      ? search.redirect
      : '/account/orders'
  })()

  const handleLogin = async (data: CustomerLoginInputType) => {
    setError('')
    try {
      const res = await apiPost<{ token: string; user: AuthUser }>('auth/login', data)
      login(res.token, res.user)
      navigate({ to: redirectTo, replace: true })
    } catch (err: unknown) {
      setError((err as Error).message || 'Invalid mobile number or password.')
    }
  }

  return (
    <CustomerAuthLayout
      title="Sign in"
      subtitle="Use your registered mobile number to continue."
    >
      {error && (
        <div className="alert alert-error flex items-center gap-2 text-sm" role="alert">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(handleLogin)} className="space-y-5">
        <div className="form-group">
          <label className="form-label" htmlFor="login-phone">Mobile number</label>
          <input
            id="login-phone"
            type="tel"
            inputMode="numeric"
            {...register('phone', {
              onChange: (e) => {
                e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
              },
            })}
            placeholder="10-digit mobile"
            autoComplete="tel"
            aria-invalid={!!errors.phone}
            className="form-input font-mono"
          />
          {errors.phone && <span className="form-error">{errors.phone.message}</span>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="login-password">Password</label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              className="form-input pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && <span className="form-error">{errors.password.message}</span>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-gold mt-2 w-full py-3.5 text-sm font-semibold tracking-wide disabled:opacity-60"
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="text-sm text-[var(--text-muted)]">
        New customer?{' '}
        <Link
          to="/account/register"
          search={redirectTo !== '/account/orders' ? { redirect: redirectTo } : {}}
          className="font-semibold text-[var(--gold-main)] hover:underline"
        >
          Create an account
        </Link>
      </p>
    </CustomerAuthLayout>
  )
}
